import { CloseIcon, WarningTwoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStepsForm } from '../../../hooks/useStepsForm';
import { apiSensedia } from '../../../services/api-sensedia';
import {
  getAgenciamentoByCorretagemAndProdType,
  getIntermediacaoByCorretagem,
  getListaCorretagensByProductType
} from '../../../utils/comissionamento.util';
import { getDGCoverIdByProductType } from '../../../utils/covers.util';
import { ProductTypes } from '../../../utils/types';
import { BoxCritical } from '../BoxCritical';
import {
  WLCobertura,
  WLConfigDependenciaContratacao,
  WLServico,
  WLTipoCondicaoContratacao,
  WLTipoCondicaoDescricaoContratacao
} from '../types/WhiteLabel';

let apiWl = apiSensedia;

if (process.env.NODE_ENV != 'development') {
  apiWl = apiSensedia;
}

type ModalSelectImageProductsProps = {
  product: {
    id_produto: number;
    descricao: string;
    cobertura: WLCobertura[];
    servicos: WLServico[];
    identifier: string;
    servicos_grouped_by_name?: {
      service_name: string;
      services: WLServico[];
    }[];
  };
  title: string;
  isOpen: boolean;
  idProduto: number;
  critica?: {
    CRITICA_NOME_PRODUTO: string;
    CRITICA_DESC_PRODUTO: string;
  };
  allowedValues: any[];
  onClose: () => void;
  getProdutosCustomizados: () => Promise<void>;
  handleSelectedProducts: (title: string) => void;
  codigoCorretor: number;
};

interface Controle {
  checked?: boolean;
  capital?: number;
  contratacao_obrigatoria?: boolean;
  possui_pendencia?: boolean;
}

interface ControleCobertura extends Controle {
  statesDoencas?: Record<number, { checked: boolean }>;
}

interface ControleServico extends Controle {
  id_servico?: number;
  descricao?: string;
}

export const ModalSelectProducts = ({
  product,
  title,
  idProduto,
  isOpen,
  critica,
  onClose,
  getProdutosCustomizados,
  handleSelectedProducts,
  codigoCorretor
}: ModalSelectImageProductsProps): JSX.Element => {
  const toast = useToast();

  // estado anterior do site
  const { state } = useStepsForm();

  // Estados da página, em ordem de aparição no modal
  const [coversState, setCoversState] = useState<
    Record<number, ControleCobertura>
  >({});

  const [capitalVariavel, setCapitalVariavel] = useState<'Sim' | 'Não'>();
  const [radioDesabilitado, setRadioDesabilitado] = useState(false);
  const [capital, setCapital] = useState('');

  const [assistsState, setAssistsState] = useState<
    Record<string, ControleServico>
  >({});

  const assistsStateById = useMemo(() => {
    return Object.values(assistsState).reduce(
      (acc: Record<number, ControleServico>, assist) => {
        acc[assist.id_servico] = assist;
        return acc;
      },
      {}
    );
  }, [assistsState]);

  const [tituloProduto, setTituloProduto] = useState<string>('');
  const [descricaoProduto, setDescricaoProduto] = useState<string>('');

  // remuneração e pagamento
  const [corretagensProduto, setCorretagensProduto] = useState<number[]>([]);
  const [corretagemSelecionada, setCorretagemSelecionada] = useState('');
  const [agenciamentoSelecionado, setAgenciamentoSelecionado] = useState('');

  // parceiro
  const [alertErroParceiro, setAlertErroParceiro] = useState(false);
  const [dataListPartner, setDataListPartner] = useState<any[]>();

  const [parceiro, setParceiro] = useState<'Sim' | 'Não'>('Não');
  const optionsForPartnerExistence: Array<typeof parceiro> = ['Sim', 'Não'];
  const [evParceiro, setEvParceiro] = useState<number | null>(null);
  const [intermediacao, setIntermediacao] = useState<string | null>(null);

  // setters e utilitarios
  const handleSelectionForPartinerAndPercentage = useCallback(
    async (value: typeof parceiro) => {
      if (value === 'Sim' && !dataListPartner) {
        try {
          const res2 = await apiWl.get(`/parceiros`);

          setDataListPartner(res2.data);
        } catch (error) {
          console.error(error);
          setAlertErroParceiro(true);
        }
      }

      setParceiro(value);
    },
    [dataListPartner]
  );

  const getCoverNameById = (coverId: number) =>
    product?.cobertura?.find((el) => el.id_cobertura === coverId).descricao;

  const getAddedCoverById = useCallback(
    (coverId: number) => {
      for (let i = 0; i < state.cover.length; i++) {
        if (state.cover[i].id_cobertura == coverId) {
          return state.cover[i];
        }
      }
    },
    [state.cover]
  );

  const isDoencaAddedToCustomProduct = useCallback(
    (idCobertura: number, idDoenca: number) => {
      for (let i = 0; i < state.cover.length; i++) {
        if (state.cover[i].id_cobertura == idCobertura) {
          const doencasAdicionadas = (state.cover[i].doencas || []) as any[];

          for (let j = 0; j < doencasAdicionadas.length; j++) {
            if (doencasAdicionadas[j].id_doenca == idDoenca) {
              return true;
            }
          }
        }
      }
    },
    [state.cover]
  );

  const getAssistAddedToCustomProduct = useCallback(
    (idAssist: number) => {
      for (let i = 0; i < state.assist.length; i++) {
        if (state.assist[i].id_servico == idAssist) {
          return state.assist[i];
        }
      }
    },
    [state.assist]
  );

  const getProductType: () => ProductTypes = useCallback(() => {
    return product?.identifier as any;
  }, [product.identifier]);

  const getAssistencesMap = useCallback(() => {
    const servicos = product?.servicos;

    const servicosGroupedByName = servicos.reduce<
      Record<string, Array<WLServico>>
    >((acc, curr) => {
      if (curr.status !== 'A') {
        return acc;
      }
      if (!acc[curr.descricao_servico]) {
        acc[curr.descricao_servico] = [];
      }

      acc[curr.descricao_servico].push(curr);

      return acc;
    }, {});

    const arrayServicosAgrupados = Object.entries(servicosGroupedByName).map(
      (entry) => {
        return {
          service_name: entry[0],
          services: entry[1]
        };
      }
    );

    return arrayServicosAgrupados;
  }, [product?.servicos]);

  const getAssistenceIdByDescriptionAndCapital = (
    servicos: Array<WLServico>,
    capitalAssistencia: number
  ) => {
    const specificAssist = servicos.filter((serv) => {
      return serv.valor_capital === capitalAssistencia;
    });

    return specificAssist[0]?.id_servico;
  };

  const getCoberturaDGType = useCallback(
    (idCobertura: number) => {
      const coberturas = product.cobertura;

      for (let i = 0; i < coberturas.length; i++) {
        if (coberturas[i].id_cobertura == idCobertura) {
          return coberturas[i].identifier;
        }
      }
    },
    [product.cobertura]
  );

  // validacoes
  const isPartnersSectionComplete = useMemo(() => {
    if (parceiro === 'Não') {
      return true;
    }

    return evParceiro && intermediacao !== '';
  }, [parceiro, evParceiro, intermediacao]);

  const isAtLeastOneOptionSelectedAssis = useMemo(() => {
    return Object.values(assistsState).some(
      ({ checked, possui_pendencia }) => checked && !possui_pendencia
    );
  }, [assistsState]);

  const isAtLeastOneBaseCoverSelected = useMemo(() => {
    const coberturasBasicas = product.cobertura.filter(
      (cob) => cob.tipo_garantia === 'B'
    );

    for (let i = 0; i < coberturasBasicas.length; i++) {
      if (coversState[coberturasBasicas[i].id_cobertura]?.checked) {
        return true;
      }
    }

    return false;
  }, [product.cobertura, coversState]);

  const areAllItemsAdded = (
    ids: number[],
    checkboxesState: Record<number, Controle>
  ) => {
    return ids.every(
      (id) =>
        checkboxesState[id]?.checked && !checkboxesState[id]?.possui_pendencia
    );
  };

  const isAtLeastOneItemAdded = (
    ids: number[],
    checkboxesState: Record<number, Controle>
  ) => {
    return ids.some(
      (id) =>
        checkboxesState[id]?.checked && !checkboxesState[id]?.possui_pendencia
    );
  };

  const validaDependencias = useCallback(
    (ids: number[], tipo: string, estado: Record<number, Controle>) => {
      if (tipo === 'N') {
        return isAtLeastOneItemAdded(ids, estado);
      }

      if (tipo === 'U') {
        return !isAtLeastOneItemAdded(ids, estado);
      }

      if (tipo === 'T') {
        return !areAllItemsAdded(ids, estado);
      }

      return false;
    },
    []
  );

  const verificaDependencias = useCallback(
    (
      deps: Array<
        WLConfigDependenciaContratacao<
          WLTipoCondicaoContratacao,
          WLTipoCondicaoDescricaoContratacao
        >
      >,
      identificador: 'coberturas_dependencia' | 'servico_dependencia',
      state: Record<number, Controle>
    ) => {
      const tipos = {
        T: null,
        N: null,
        U: null
      };

      deps.forEach((config_dependencia) => {
        const ids = config_dependencia[identificador].map((cob) => {
          return cob.id_cobertura ?? cob.idServico;
        });

        tipos[config_dependencia.tipo_condicao] = validaDependencias(
          ids,
          config_dependencia.tipo_condicao,
          state
        );
      });

      if (tipos.T === null && tipos.U !== null) {
        return tipos.N || tipos.U;
      }

      if (tipos.T !== null && tipos.U === null) {
        return tipos.N || tipos.T;
      }

      return tipos.N || (tipos.T && tipos.U);
    },
    [validaDependencias]
  );

  const validateDependenciaServicoCobertura = useCallback(
    (
      codigo_servico: number,
      coberturas: Array<WLCobertura>,
      estadoCoberturas: Record<number, ControleCobertura>
    ) => {
      const coberturasQueFornecemOServico = coberturas.filter((cobertura) => {
        const codigosServicos = cobertura.servicos.map(
          (coberturaServico) => coberturaServico.codigo_servico
        );

        return codigosServicos.includes(codigo_servico);
      });

      if (coberturasQueFornecemOServico.length <= 0) return false; // não tem dependencias

      const idsCoberturas = coberturasQueFornecemOServico.map(
        (el) => el.id_cobertura
      );

      // pelo menos uma das coberturas que fornecem o servico deve estar selecionada
      // Se existe não há pendencias
      return !idsCoberturas.some(
        (id) =>
          estadoCoberturas[id]?.checked &&
          // se possui pendencias não deve ser considerada selecinada
          !estadoCoberturas[id]?.possui_pendencia
      );
    },
    []
  );

  const isAtLeastOneCoverSelected = useMemo(() => {
    const coberturasSelecionadas = Object.entries(coversState)
      .filter(([, { checked }]) => {
        return checked;
      })
      .map(([idCobertura, { capital }]) => {
        return {
          id_cobertura: idCobertura,
          capital
        };
      });

    const maisDeUmaCoberturaSelecionada = coberturasSelecionadas.length > 0;
    const peloMenosUmaCoberturaSelecionadaSemCapital =
      coberturasSelecionadas.find((cobertura) => {
        return !cobertura.capital;
      });

    return (
      maisDeUmaCoberturaSelecionada &&
      !peloMenosUmaCoberturaSelecionadaSemCapital
    );
  }, [coversState]);

  // inicio
  const iniciado = useRef(false);
  useEffect(() => {
    if (iniciado.current) return;

    window.scrollTo(0, 0);
    product.servicos_grouped_by_name = getAssistencesMap();

    // popular coberturas
    const coversStates = coversState;

    if (product && product.cobertura) {
      product.cobertura.forEach((item) => {
        const coberturaNoProduto = getAddedCoverById(item.id_cobertura);

        if (coberturaNoProduto) {
          coversStates[item.id_cobertura] = {
            checked: true,
            contratacao_obrigatoria: coberturaNoProduto.contratacao_obrigatoria,
            capital: coberturaNoProduto.capital,
            statesDoencas:
              item.doencas?.length > 0
                ? (() => {
                    const objStateDoencas = {};

                    item.doencas.forEach((doenca) => {
                      objStateDoencas[doenca.id_doenca] = {
                        checked: isDoencaAddedToCustomProduct(
                          item.id_cobertura,
                          doenca.id_doenca
                        ) //<-- (doencas que foram adicionadas) verificar se está adicionado ou não
                      };
                    });

                    return objStateDoencas;
                  })()
                : undefined
          };
        } else {
          coversStates[item.id_cobertura] = {
            checked:
              (item.is_cobertura_morte ||
                item.binded_to_death_cob ||
                item.required) &&
              idProduto === 0,
            contratacao_obrigatoria:
              (item.is_cobertura_morte ||
                item.binded_to_death_cob ||
                item.required) &&
              idProduto === 0,
            capital: item.is_cobertura_morte
              ? item.capitais_disponiveis[item.capitais_disponiveis.length - 1]
              : undefined,
            statesDoencas:
              item.doencas?.length > 0
                ? (() => {
                    const objStateDoencas = {};

                    (item.doencas as any[]).forEach((doenca) => {
                      objStateDoencas[doenca.id_doenca] = {
                        checked: idProduto === 0 //<-- se for edição, as doenças iniciam desmarcadas com exceção das que foram adicionadas
                      };
                    });

                    return objStateDoencas;
                  })()
                : undefined
          };
        }

        if (item.is_cobertura_morte) {
          setCapital(
            `${item.capitais_disponiveis[item.capitais_disponiveis.length - 1]}`
          );
        }
      });
    }

    setCoversState({ ...coversStates });

    // popular capital variável
    setCapitalVariavel(
      (state.capitalMin && state.capitalMax) || state.hasVariableCapital
        ? 'Sim'
        : 'Não'
    );

    // popular servicos
    const assistStates = assistsState;

    if (product && product.servicos_grouped_by_name) {
      if (idProduto !== 0) {
        product.servicos_grouped_by_name.forEach((item) => {
          item.services.forEach((serv) => {
            const assist = getAssistAddedToCustomProduct(serv.id_servico);

            if (assist) {
              assistStates[item.service_name] = {
                checked: true,
                id_servico: assist.id_servico,
                capital: serv.valor_capital,
                descricao: serv.descricao_servico,
                contratacao_obrigatoria:
                  (Boolean(serv.default_checked) &&
                    !serv.edit_access_evs?.includes(codigoCorretor)) ||
                  assist.contratacao_obrigatoria
              };
            } else {
              if (!assistStates[item.service_name]?.checked) {
                assistStates[item.service_name] = {
                  checked:
                    Boolean(serv.default_checked) &&
                    !serv.edit_access_evs?.includes(codigoCorretor),
                  id_servico: serv.id_servico,
                  capital: null,
                  descricao: serv.descricao_servico,
                  contratacao_obrigatoria:
                    Boolean(serv.default_checked) &&
                    !serv.edit_access_evs?.includes(codigoCorretor)
                };
              }
            }
          });
        });
      } else {
        product.servicos_grouped_by_name.forEach((item) => {
          assistStates[item.service_name] = {
            checked: Boolean(
              item.services[0].default_checked &&
                !item.services[0].edit_access_evs?.includes(codigoCorretor)
            ),
            contratacao_obrigatoria: Boolean(
              item.services[0].default_checked &&
                !item.services[0].edit_access_evs?.includes(codigoCorretor)
            ),
            id_servico: item.services[0].id_servico,
            capital: item.services[0].valor_capital,
            descricao: item.services[0].descricao_servico
          };
        });
      }
    }
    setAssistsState({ ...assistStates });

    // popular nome e titulo do produto
    setTituloProduto(state.nameProduct);
    setDescricaoProduto(state.descriptionProduct);

    // popular remuneracao
    setCorretagensProduto(getListaCorretagensByProductType(getProductType()));
    setCorretagemSelecionada(state.brokerage);
    setAgenciamentoSelecionado(state.agency);

    // popular informacao do parceiro
    if (state.partner) {
      setParceiro(state.partner ? 'Sim' : 'Não');
      setEvParceiro(state.registeredPartner);
      setIntermediacao(state.percentage);

      // lida com as intermediacoes disponíveis
      const intermediacoes = getIntermediacaoByCorretagem(
        Number(state.brokerage)
      );
      if (!intermediacoes.includes(Number(state.percentage))) {
        setIntermediacao(`${intermediacoes[intermediacoes.length - 1]}`);
      }
    }

    handleSelectionForPartinerAndPercentage(state.partner ? 'Sim' : 'Não');

    iniciado.current = true;
  }, [
    assistsState,
    codigoCorretor,
    coversState,
    getAddedCoverById,
    getAssistAddedToCustomProduct,
    getAssistencesMap,
    getProductType,
    handleSelectionForPartinerAndPercentage,
    idProduto,
    isDoencaAddedToCustomProduct,
    product,
    state
  ]);

  // observa estados de coberturas e servicos para atualizar seus estados de dependências
  useEffect(() => {
    if (iniciado.current) {
      // coberturas
      let coberturasModificadas = false;
      product.cobertura.forEach((cobertura) => {
        // Valida seleções
        const pendencias = verificaDependencias(
          cobertura.config_dependencia_contratacao,
          'coberturas_dependencia',
          coversState
        );

        if (
          coversState[cobertura.id_cobertura].possui_pendencia !== pendencias
        ) {
          coberturasModificadas = true;
          coversState[cobertura.id_cobertura].possui_pendencia = pendencias;
        }

        // valida capital
        // busca coberturas que tem bind_to_cob apontando para a cobertura atual
        const coberturasVinculadas = product.cobertura.filter(
          (coberturaVinculada) =>
            coberturaVinculada.binded_to_cob === cobertura.id_cobertura
        );

        // ajusta o capital das coberturas vinculadas
        coberturasVinculadas.forEach((el) => {
          if (
            coversState[el.id_cobertura].capital !==
            coversState[cobertura.id_cobertura].capital
          ) {
            coberturasModificadas = true;
            coversState[el.id_cobertura].capital =
              coversState[cobertura.id_cobertura].capital;
          }
        });
      });

      if (coberturasModificadas) {
        setCoversState({ ...coversState });
      }

      // servicos
      let servicosModificados = false;
      product.servicos_grouped_by_name.forEach(({ service_name, services }) => {
        const pendencias =
          verificaDependencias(
            services[0].config_dependencia_contratacao_servico,
            'servico_dependencia',
            assistsStateById
          ) ||
          validateDependenciaServicoCobertura(
            services[0].codigo_servico,
            product.cobertura,
            coversState
          );

        if (assistsState[service_name].possui_pendencia !== pendencias) {
          servicosModificados = true;
          assistsState[service_name].possui_pendencia = pendencias;
        }
      });

      if (servicosModificados) {
        setAssistsState({ ...assistsState });
      }
    }
  }, [
    assistsState,
    assistsStateById,
    coversState,
    product.cobertura,
    product.servicos_grouped_by_name,
    validateDependenciaServicoCobertura,
    verificaDependencias
  ]);

  const configValida = useMemo(() => {
    let configuracaoValida =
      isAtLeastOneOptionSelectedAssis &&
      isAtLeastOneCoverSelected &&
      isAtLeastOneBaseCoverSelected &&
      tituloProduto.length > 0 &&
      descricaoProduto.length > 0 &&
      corretagemSelecionada != '' &&
      agenciamentoSelecionado != '';

    if (getProductType() === 'acidentes_pessoais') {
      configuracaoValida = capital.toString().length > 0 && configuracaoValida;
    }

    return configuracaoValida;
  }, [
    isAtLeastOneOptionSelectedAssis,
    isAtLeastOneCoverSelected,
    isAtLeastOneBaseCoverSelected,
    tituloProduto.length,
    descricaoProduto.length,
    corretagemSelecionada,
    agenciamentoSelecionado,
    getProductType,
    capital
  ]);

  // funcao enviar
  const handlePostProdutosCostumizados = useCallback(
    async (cob: any[], assists: any[]) => {
      try {
        const idsCoberturasProdBase = product.cobertura.reduce((acc, el) => {
          acc.add(el.id_cobertura);
          return acc;
        }, new Set());

        const idsServicosProdBase = product.servicos.reduce((acc, el) => {
          acc.add(el.id_servico);
          return acc;
        }, new Set());

        const coberturasNoProdutobase = cob.filter((el) =>
          idsCoberturasProdBase.has(el.id_cobertura)
        );
        const assistenciasNoProdutoBase = assists.filter((el) =>
          idsServicosProdBase.has(el.id_servico)
        );

        const body: any = {
          website: location.pathname.split('/')[4],
          id_produto: product.id_produto, // <- id do produto base
          nome_produto: product.descricao, // <- nome do produto base
          sub_titulo: tituloProduto,
          descricao_produto: descricaoProduto,
          possui_capital_variavel: capitalVariavel === 'Sim',
          recomendado: false,
          coberturas: coberturasNoProdutobase,
          servicos: assistenciasNoProdutoBase,
          valor_renda: parseInt(capital),
          valor_minimo_contrata: parseInt(state.capitalMin),
          valor_maximo_contrata: parseInt(state.capitalMax),
          corretagem: corretagemSelecionada,
          agenciamento: agenciamentoSelecionado,
          ev_parceria: evParceiro,
          intermediacao
        };

        if (idProduto !== 0) {
          body.product_id = state.idProduct; // <- id do produto customizado
        }

        await apiWl.post(`/produtos-customizados`, body);
        getProdutosCustomizados();
      } catch (error) {
        console.error(error);
      }
    },
    [
      agenciamentoSelecionado,
      capital,
      capitalVariavel,
      corretagemSelecionada,
      descricaoProduto,
      evParceiro,
      getProdutosCustomizados,
      idProduto,
      intermediacao,
      product.cobertura,
      product.descricao,
      product.id_produto,
      product.servicos,
      state.capitalMax,
      state.capitalMin,
      state.idProduct,
      tituloProduto
    ]
  );

  const onSubmit = useCallback(async () => {
    const coberturasSelecionadas = Object.entries(coversState)
      .filter(([, { checked, possui_pendencia }]) => {
        return checked && !possui_pendencia;
      })
      .map(([idCobertura, stateCobertura]) => {
        let id_cobertura = Number(idCobertura);
        const coverAddtionalAttrs = {};

        if (stateCobertura.statesDoencas) {
          coverAddtionalAttrs['doencas'] = Object.entries(
            stateCobertura.statesDoencas
          )
            .filter(([, { checked }]) => checked)
            .map(([id]) => ({ id_doenca: Number(id) }));

          coverAddtionalAttrs['dg_pack_type'] =
            getCoberturaDGType(id_cobertura);

          id_cobertura = getDGCoverIdByProductType(getProductType());
        }

        const cobertura = {
          id_cobertura,
          contratacao_obrigatoria: stateCobertura.contratacao_obrigatoria,
          capital: stateCobertura.capital,
          ...coverAddtionalAttrs
        };

        return cobertura;
      });

    const assistenciasSelecionadas = Object.values(assistsState)
      .filter(({ checked, possui_pendencia }) => checked && !possui_pendencia)
      .map((itemServ) => {
        const servico = {
          id_servico: itemServ.id_servico,
          descricao_servico: itemServ.descricao,
          contratacao_obrigatoria: itemServ.contratacao_obrigatoria
        };

        return servico;
      });

    handlePostProdutosCostumizados(
      coberturasSelecionadas,
      assistenciasSelecionadas
    );
    handleSelectedProducts(tituloProduto.length > 0 ? tituloProduto : title);
    onClose();
  }, [
    assistsState,
    coversState,
    getCoberturaDGType,
    getProductType,
    handlePostProdutosCostumizados,
    handleSelectedProducts,
    onClose,
    title,
    tituloProduto
  ]);

  useEffect(() => {
    if (alertErroParceiro) {
      setTimeout(() => {
        setAlertErroParceiro(false);
        setParceiro('Não');
        setEvParceiro(null);
        setIntermediacao(null);
      }, 3500);
    }
  }, [alertErroParceiro]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="930px !important">
        <ModalHeader pb="40px" pt="30px">
          <Text color="#323232" fontSize="1.25rem">
            {product.descricao}
          </Text>
        </ModalHeader>
        <ModalCloseButton
          _hover={{}}
          _active={{}}
          _focus={{
            '&:focus': {
              boxShadow: 'none !important'
            }
          }}
        />
        <ModalBody pt="0 !important" pl="0" pr="0" pb="30px">
          <Box>
            <Box pl="30px" pr="12px" width="100%">
              <Box>
                <Box width="100%">
                  <Box
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Text
                      p="0 0 0 10% !important"
                      color="#001E64"
                      fontWeight="bold"
                      w="100%"
                    >
                      Coberturas
                    </Text>

                    <Text
                      m="0 !important"
                      p="0 0  0 32px"
                      color="#001E64"
                      fontWeight="bold"
                      maxW="290px"
                      w="100%"
                    >
                      Capital
                    </Text>
                  </Box>
                </Box>
                <Box
                  aria-label="group"
                  mb="40px"
                  display="flex"
                  justifyContent="space-between"
                  h="fit-content"
                  overflowY="scroll"
                  sx={{
                    '&::-webkit-scrollbar': {
                      width: '13px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      border: '1px solid #FFFFFF',
                      borderRadius: '24px',
                      backgroundColor: '#D9D9D9'
                    }
                  }}
                >
                  <Box
                    w="100%"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    gap="8px"
                    m="15px 8px 0 0 !important"
                    h="max-content"
                  >
                    {product?.cobertura?.map((group, index) => {
                      const coberturaNoProdutoPersonalizado = getAddedCoverById(
                        group.id_cobertura
                      );
                      // const hasUnfulfilledDependencies = verificaDependencias(
                      //   group.config_dependencia_contratacao,
                      //   'coberturas_dependencia',
                      //   coversState
                      // );

                      if (group.status === 'S') return <></>;

                      return (
                        <Box
                          m="15px 0 0 0 !importante"
                          key={index}
                          w="100%"
                          h="max-content"
                          p="9px 30px 9px 25px"
                          borderLeft="5px solid #001E64"
                          display="flex"
                          flexDirection="column"
                          alignItems="start"
                          borderRadius="6px"
                          bg={
                            index % 2 === 0
                              ? '#F4F4F4'
                              : 'rgba(211, 222, 237, 0.5)'
                          }
                        >
                          <HStack
                            justifyContent="space-between"
                            width="100%"
                            alignItems="start"
                          >
                            <Box>
                              <Box
                                height="38px"
                                display="flex"
                                alignItems="center"
                              >
                                <Switch
                                  defaultChecked={group.required}
                                  isDisabled={
                                    group.required ||
                                    coversState[group.id_cobertura]
                                      ?.possui_pendencia
                                  }
                                  id={
                                    group.id_cobertura.toString() +
                                    '/' +
                                    group.descricao
                                  }
                                  value={group.descricao}
                                  colorScheme="blue"
                                  size="lg"
                                  isChecked={
                                    !coversState[group.id_cobertura]
                                      ?.possui_pendencia &&
                                    coversState[group.id_cobertura]?.checked
                                  }
                                  onChange={() => {
                                    coversState[group.id_cobertura].checked =
                                      !coversState[group.id_cobertura]?.checked;

                                    setCoversState({
                                      ...coversState
                                    });
                                  }}
                                  sx={{
                                    '.chakra-switch__thumb[data-checked]': {
                                      transform: 'translateX(32px)',
                                      backgroundColor: '#001E64'
                                    },
                                    '.chakra-switch__thumb': {
                                      transform: 'translateX(-8px)',
                                      height: '32px',
                                      width: '32px'
                                    },
                                    '.chakra-switch__track[data-checked]': {
                                      backgroundColor: '#001E6466'
                                    },
                                    '.chakra-switch__track:focus': {
                                      boxShadow: 'none'
                                    },
                                    '.chakra-switch__track': {
                                      height: '24px',
                                      width: '56px',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }
                                  }}
                                />
                                <Text
                                  ml="25px"
                                  color="#001E64"
                                  noOfLines={1}
                                  title={
                                    group.descricao.length > 50
                                      ? group.descricao
                                      : undefined
                                  }
                                >
                                  {group.descricao}
                                </Text>
                                {!group.required &&
                                  !coversState[group.id_cobertura]
                                    ?.possui_pendencia &&
                                  coversState[group.id_cobertura]?.checked && (
                                    <Checkbox
                                      defaultChecked={
                                        coberturaNoProdutoPersonalizado?.contratacao_obrigatoria
                                      }
                                      onChange={(e) => {
                                        setCoversState((estado) => {
                                          estado[
                                            group.id_cobertura
                                          ].contratacao_obrigatoria =
                                            e.target.checked;

                                          return { ...estado };
                                        });
                                      }}
                                      className="product-checkbox cover-checkbox tw-mx-6"
                                      colorScheme="orange"
                                      sx={{
                                        '.chakra-checkbox__control': {
                                          width: '24px',
                                          height: '24px'
                                        }
                                      }}
                                    >
                                      Obrigatória
                                    </Checkbox>
                                  )}
                              </Box>
                              {/* lista de doenças */}
                              {group.doencas?.length > 0 ? (
                                <VStack
                                  alignItems="start"
                                  bg="transparent"
                                  p="8px"
                                  ml="58px"
                                  mt="20px"
                                  width="100%"
                                  maxW="501px"
                                  className="tabela-doencas"
                                >
                                  {group.doencas.map(
                                    (doenca: any, index: any) => {
                                      return (
                                        <HStack key={index} width="100%">
                                          <Box
                                            borderBottom={
                                              index >= group.doencas.length - 1
                                                ? undefined
                                                : '1px solid #D3DEED'
                                            }
                                            width="100%"
                                            pb="10px"
                                          >
                                            {/* parei aqui: indo fazer a recuperação das doençs selecionadas na função de enviar */}
                                            <Checkbox
                                              id={'doenca-' + doenca.id_doenca}
                                              className="doenca-checkbox"
                                              colorScheme="orange"
                                              sx={{
                                                '.chakra-checkbox__control': {
                                                  width: '24px',
                                                  height: '24px',
                                                  border: '2px solid #001E64'
                                                }
                                              }}
                                              isChecked={
                                                doenca.obrigatoria ||
                                                coversState[group.id_cobertura]
                                                  ?.statesDoencas[
                                                  doenca.id_doenca
                                                ]?.checked
                                              }
                                              isDisabled={
                                                doenca.obrigatoria || undefined
                                              }
                                              onChange={() => {
                                                coversState[
                                                  group.id_cobertura
                                                ].statesDoencas[
                                                  doenca.id_doenca
                                                ].checked =
                                                  !coversState[
                                                    group.id_cobertura
                                                  ].statesDoencas[
                                                    doenca.id_doenca
                                                  ]?.checked;

                                                setCoversState({
                                                  ...coversState
                                                });
                                              }}
                                            >
                                              <Text color="#001E64">
                                                {doenca.descricao}
                                              </Text>
                                            </Checkbox>
                                          </Box>
                                        </HStack>
                                      );
                                    }
                                  )}
                                </VStack>
                              ) : (
                                <></>
                              )}
                            </Box>
                            {(() => {
                              if (group.binded_to_cob) {
                                if (getProductType() == 'acidentes_pessoais') {
                                  return (
                                    <Select
                                      key={index}
                                      className={
                                        'binding-' +
                                        group.binded_to_cob +
                                        ' ' +
                                        'cobertura-' +
                                        group.id_cobertura
                                      }
                                      isDisabled={!!group.binded_to_cob}
                                      maxW="160px"
                                      w="100%"
                                      h="38px"
                                      placeholder={'Capital'}
                                      border="1px solid #001E64 !important"
                                      borderRadius="6px"
                                      bg="#FFFFFF"
                                      _hover={{}}
                                      _active={{}}
                                      _focus={{
                                        '&:focus': {
                                          boxShadow: 'none !important'
                                        }
                                      }}
                                      value={
                                        coversState[group.binded_to_cob]
                                          ?.capital
                                      }
                                    >
                                      {group.capitais_disponiveis?.map(
                                        (value) => {
                                          return (
                                            <option key={value} value={value}>
                                              {formatarParaMoeda(value)}
                                            </option>
                                          );
                                        }
                                      )}
                                    </Select>
                                  );
                                }

                                if (getProductType() === 'vida_flex') {
                                  return (
                                    <Input
                                      key={index}
                                      className={
                                        'binding-' +
                                        group.binded_to_cob +
                                        ' ' +
                                        'cobertura-' +
                                        group.id_cobertura
                                      }
                                      isDisabled={!!group.binded_to_cob}
                                      maxW="160px"
                                      w="100%"
                                      h="38px"
                                      placeholder={'R$ 0,00'}
                                      border="1px solid #001E64 !important"
                                      borderRadius="6px"
                                      bg="#FFFFFF"
                                      _hover={{}}
                                      _active={{}}
                                      _focus={{
                                        '&:focus': {
                                          boxShadow: 'none !important'
                                        }
                                      }}
                                      value={
                                        coversState[group.binded_to_cob]
                                          ?.capital
                                      }
                                    />
                                  );
                                }
                              } else {
                                if (
                                  (getProductType() === 'vida_flex' ||
                                    getProductType() == 'acidentes_pessoais') &&
                                  group.valores_capital?.length > 0
                                ) {
                                  return (
                                    <Select
                                      key={index}
                                      className={
                                        'cobertura-' + group.id_cobertura
                                      }
                                      maxW="160px"
                                      w="100%"
                                      h="38px"
                                      placeholder={'Capital'}
                                      border="1px solid #001E64 !important"
                                      borderRadius="6px"
                                      bg="#FFFFFF"
                                      _hover={{
                                        border: '1px solid #FF5000 !important'
                                      }}
                                      _focus={{
                                        border: '1px solid #FF5000 !important'
                                      }}
                                      transition="0.2s"
                                      value={
                                        coversState[group.id_cobertura]?.capital
                                      }
                                      onChange={(
                                        e: React.ChangeEvent<HTMLSelectElement>
                                      ) => {
                                        setRadioDesabilitado(
                                          !Number(e.target.value)
                                        );

                                        if (group.is_cobertura_morte) {
                                          setCapital(e.target.value);
                                        }

                                        // busca coberturas que tem bind_to_cob apontando para a cobertura atual
                                        const coberturasVinculadas =
                                          product.cobertura.filter(
                                            (cobertura) =>
                                              cobertura.binded_to_cob ===
                                              group.id_cobertura
                                          );

                                        // ajusta o capital das coberturas vinculadas
                                        coberturasVinculadas.forEach((el) => {
                                          let coberturaVinculada =
                                            coversState[el.id_cobertura];

                                          if (!coberturaVinculada) {
                                            coberturaVinculada = {
                                              capital: Number(e.target.value)
                                            };
                                          } else {
                                            coberturaVinculada.capital = Number(
                                              e.target.value
                                            );
                                          }

                                          coversState[el.id_cobertura] =
                                            coberturaVinculada;
                                        });

                                        coversState[
                                          group.id_cobertura
                                        ].capital = Number(e.target.value);

                                        setCoversState({ ...coversState });
                                      }}
                                    >
                                      {group.capitais_disponiveis?.map(
                                        (value) => {
                                          return (
                                            <option key={value} value={value}>
                                              {formatarParaMoeda(value)}
                                            </option>
                                          );
                                        }
                                      )}
                                    </Select>
                                  );
                                }
                                if (
                                  (getProductType() === 'vida_flex' ||
                                    getProductType() == 'acidentes_pessoais') &&
                                  (!group.valores_capital ||
                                    group.valores_capital?.length <= 0)
                                ) {
                                  return (
                                    <Input
                                      key={index}
                                      className={
                                        'cobertura-' + group.id_cobertura
                                      }
                                      maxW="160px"
                                      w="100%"
                                      h="38px"
                                      placeholder={'R$ 0,00'}
                                      border="1px solid #001E64 !important"
                                      borderRadius="6px"
                                      bg="#FFFFFF"
                                      _hover={{
                                        border: '1px solid #FF5000 !important'
                                      }}
                                      _focus={{
                                        border: '1px solid #FF5000 !important'
                                      }}
                                      transition="0.2s"
                                      value={formatarParaMoeda(
                                        coversState[group.id_cobertura]
                                          ?.capital ?? 0
                                      )}
                                      onBlur={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) => {
                                        const valor =
                                          Number(
                                            e.target.value.replace(/\D/g, '')
                                          ) / 100;

                                        if (
                                          valor <
                                          Math.min(
                                            ...group.capitais_disponiveis
                                          )
                                        ) {
                                          toast({
                                            position: 'top',
                                            description: `Valor digitado não pode ser menor que o capital mínimo permitido pela cobertura: ${formatarParaMoeda(
                                              Math.min(
                                                ...group.capitais_disponiveis
                                              )
                                            )}`,
                                            duration: 4000,
                                            isClosable: true,
                                            status: 'warning'
                                          });
                                          coversState[
                                            group.id_cobertura
                                          ].capital = Math.min(
                                            ...group.capitais_disponiveis
                                          );

                                          setCoversState({
                                            ...coversState
                                          });
                                        }

                                        if (group.limits_covers) {
                                          let greaterValue = 0;
                                          let isSmaller = false;
                                          group.limits_covers.forEach(
                                            (limCoverId: number) => {
                                              if (
                                                valor <
                                                coversState[limCoverId].capital
                                              ) {
                                                isSmaller = true;
                                                if (
                                                  coversState[limCoverId]
                                                    .capital > greaterValue
                                                ) {
                                                  greaterValue =
                                                    coversState[limCoverId]
                                                      .capital;
                                                }

                                                toast({
                                                  position: 'top',
                                                  description: `O valor do capital da cobertura ${getCoverNameById(
                                                    group.id_cobertura
                                                  )} não pode ser menor do que o da cobertura ${getCoverNameById(
                                                    limCoverId
                                                  )}`,
                                                  duration: 4000,
                                                  isClosable: true,
                                                  status: 'warning'
                                                });
                                              }
                                            }
                                          );

                                          if (isSmaller) {
                                            coversState[
                                              group.id_cobertura
                                            ].capital = greaterValue;

                                            setCoversState({
                                              ...coversState
                                            });
                                          }
                                        }
                                      }}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLSelectElement>
                                      ) => {
                                        const valor =
                                          Number(
                                            e.target.value.replace(/\D/g, '')
                                          ) / 100;

                                        if (
                                          valor >
                                          Math.max(
                                            ...group.capitais_disponiveis
                                          )
                                        ) {
                                          toast({
                                            position: 'top',
                                            description: `Valor digitado não pode ser maior que o capital máximo permitido pela cobertura: ${formatarParaMoeda(
                                              Math.max(
                                                ...group.capitais_disponiveis
                                              )
                                            )}`,
                                            duration: 4000,
                                            isClosable: true,
                                            status: 'warning'
                                          });

                                          return;
                                        }

                                        if (group.limited_by_covers) {
                                          let hasErr = false;
                                          group.limited_by_covers.forEach(
                                            (limCoverId: number) => {
                                              if (
                                                valor >
                                                coversState[limCoverId].capital
                                              ) {
                                                toast({
                                                  position: 'top',
                                                  description: `O valor do capital da cobertura ${getCoverNameById(
                                                    group.id_cobertura
                                                  )} não pode ser maior do que o da cobertura ${getCoverNameById(
                                                    limCoverId
                                                  )}`,
                                                  duration: 4000,
                                                  isClosable: true,
                                                  status: 'warning'
                                                });

                                                hasErr = true;
                                              }
                                            }
                                          );

                                          if (hasErr) return;
                                        }

                                        setRadioDesabilitado(!valor);

                                        if (group.is_cobertura_morte) {
                                          setCapital(valor.toString());
                                        }

                                        // busca coberturas que tem bind_to_cob apontando para a cobertura atual
                                        const coberturasVinculadas =
                                          product.cobertura.filter(
                                            (cobertura) =>
                                              cobertura.binded_to_cob ===
                                              group.id_cobertura
                                          );

                                        // ajusta o capital das coberturas vinculadas
                                        coberturasVinculadas.forEach((el) => {
                                          let coberturaVinculada =
                                            coversState[el.id_cobertura];

                                          if (!coberturaVinculada) {
                                            coberturaVinculada = {
                                              capital: valor
                                            };
                                          } else {
                                            coberturaVinculada.capital = valor;
                                          }

                                          coversState[el.id_cobertura] =
                                            coberturaVinculada;
                                        });

                                        coversState[
                                          group.id_cobertura
                                        ].capital = valor;

                                        setCoversState({ ...coversState });
                                      }}
                                    />
                                  );
                                }
                              }
                            })()}
                          </HStack>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box pr="30px" pl="30px">
            <TableContainer
              borderRadius="15px"
              p="30px 6px 14px 6px"
              bg="#F4F4F4"
            >
              <Table>
                <Thead>
                  <Tr>
                    <Th border="none" color="#323232">
                      Deseja permitir que o cliente altere o capital?
                    </Th>
                  </Tr>
                </Thead>

                <Tbody>
                  <Tr>
                    <Td border="none">
                      <RadioGroup
                        defaultValue={capitalVariavel}
                        onChange={(e: 'Sim' | 'Não') => {
                          setCapitalVariavel(e);
                        }}
                      >
                        <Stack direction="row" gap="28px">
                          <Radio
                            colorScheme="orange"
                            value="Sim"
                            isDisabled={radioDesabilitado}
                          >
                            Sim
                          </Radio>

                          <Radio
                            colorScheme="orange"
                            value="Não"
                            isDisabled={radioDesabilitado}
                          >
                            Não
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          <Box pr="30px" pl="30px" m="30px 0 15px 0  !important">
            <Box width="100%">
              <Box width="100%" display="flex" justifyContent="space-between">
                <Text
                  p="0 0 0 10% !important"
                  color="#001E64"
                  fontWeight="bold"
                  w="100%"
                >
                  Assistências
                </Text>

                <Text
                  m="0 !important"
                  p="0 0  0 32px"
                  color="#001E64"
                  fontWeight="bold"
                  maxW="290px"
                  w="100%"
                >
                  Capital
                </Text>
              </Box>
            </Box>

            <Box
              aria-label="group"
              mb="40px"
              display="flex"
              justifyContent="space-between"
              h="fit-content"
              overflowY="scroll"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '13px'
                },
                '&::-webkit-scrollbar-thumb': {
                  border: '1px solid #FFFFFF',
                  borderRadius: '24px',
                  backgroundColor: '#D9D9D9'
                }
              }}
            >
              <Box
                w="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                gap="8px"
                m="15px 8px 0 0 !important"
                h="max-content"
              >
                {product?.servicos_grouped_by_name?.map(
                  ({ service_name, services }, index) => {
                    const editAccess =
                      services[0].edit_access_evs?.includes(codigoCorretor);
                    const servicoObrigatorio = services[0].default_checked;

                    return (
                      <Box
                        m="15px 0 0 0 !importante"
                        key={index}
                        w="100%"
                        h="48px"
                        p="10px 30px 10px 25px"
                        borderLeft="5px solid #001E64"
                        display="flex"
                        alignItems="center"
                        borderRadius="6px"
                        bg={
                          index % 2 === 0
                            ? '#F4F4F4'
                            : 'rgba(211, 222, 237, 0.5)'
                        }
                      >
                        <Switch
                          defaultChecked={assistsState[service_name]?.checked}
                          isDisabled={
                            (servicoObrigatorio && !editAccess) ||
                            assistsState[service_name]?.possui_pendencia
                          }
                          id={
                            services[0].id_servico.toString() +
                            '/' +
                            services[0].descricao_servico
                          }
                          value={services[0].id_servico.toString()}
                          className="product-checkbox"
                          colorScheme="blue"
                          size="lg"
                          isChecked={
                            !assistsState[service_name]?.possui_pendencia &&
                            assistsState[service_name]?.checked
                          }
                          onChange={() => {
                            assistsState[service_name].checked =
                              !assistsState[service_name]?.checked;

                            setAssistsState({
                              ...assistsState
                            });
                          }}
                          sx={{
                            '.chakra-switch__thumb[data-checked]': {
                              transform: 'translateX(32px)',
                              backgroundColor: '#001E64'
                            },
                            '.chakra-switch__thumb': {
                              transform: 'translateX(-8px)',
                              height: '32px',
                              width: '32px'
                            },
                            '.chakra-switch__track[data-checked]': {
                              backgroundColor: '#001E6466'
                            },
                            '.chakra-switch__track:focus': {
                              boxShadow: 'none'
                            },
                            '.chakra-switch__track': {
                              height: '24px',
                              width: '56px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                        <Text
                          ml="25px"
                          color="#001E64"
                          noOfLines={1}
                          title={
                            service_name.length > 35 ? service_name : undefined
                          }
                        >
                          {service_name}
                        </Text>
                        {!servicoObrigatorio &&
                          !assistsState[service_name]?.possui_pendencia &&
                          assistsState[service_name]?.checked && (
                            <Checkbox
                              defaultChecked={
                                assistsState[service_name]
                                  .contratacao_obrigatoria
                              }
                              checked={
                                assistsState[service_name]
                                  .contratacao_obrigatoria
                              }
                              onChange={(e) => {
                                setAssistsState((assistState) => {
                                  assistState[
                                    service_name
                                  ].contratacao_obrigatoria = e.target.checked;

                                  return { ...assistState };
                                });
                              }}
                              className="product-checkbox cover-checkbox tw-ml-6"
                              colorScheme="orange"
                              sx={{
                                '.chakra-checkbox__control': {
                                  width: '24px',
                                  height: '24px'
                                }
                              }}
                            >
                              Obrigatório
                            </Checkbox>
                          )}
                      </Box>
                    );
                  }
                )}
              </Box>
              <Box
                maxW="290px"
                w="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                gap="8px"
                m="15px 0 0 0 !important"
                h="max-content"
              >
                {product?.servicos_grouped_by_name?.map((group) => {
                  return (
                    <Select
                      key={group.service_name}
                      className={'servico-' + group.service_name}
                      disabled={!group.services[0].valor_capital}
                      w="100%"
                      h="48px"
                      placeholder={'Capital'}
                      border="1px solid #001E64 !important"
                      borderRadius="6px"
                      bg="#FFFFFF"
                      _hover={{
                        border: '1px solid #FF5000 !important'
                      }}
                      _focus={{
                        border: '1px solid #FF5000 !important'
                      }}
                      transition="0.2s"
                      value={assistsState[group.service_name]?.capital}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = Number(e.target.value);
                        assistsState[group.service_name].capital = value;

                        const novoIdServico =
                          getAssistenceIdByDescriptionAndCapital(
                            group.services,
                            value
                          );

                        assistsState[group.service_name].id_servico =
                          novoIdServico;

                        setAssistsState({
                          ...assistsState
                        });
                      }}
                    >
                      {group.services?.map((serv) => {
                        return (
                          <option
                            key={`option-serv-${serv.valor_capital}`}
                            value={serv.valor_capital}
                          >
                            {formatarParaMoeda(serv.valor_capital)}
                          </option>
                        );
                      })}
                    </Select>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box pr="30px" pl="30px">
            <Text color="#323232" fontSize="1.25rem" fontWeight="bold">
              Personalize aqui o nome do seu produto:
            </Text>
            <VStack w="100%" mt="20px" gap="30px">
              <Box
                w="100%"
                bg="rgba(244, 244, 244, 0.7)"
                borderRadius="15px"
                p="30px"
              >
                <Text fontSize="1rem">Digite o nome do seu produto*</Text>
                <VStack alignItems="end" maxW="400px">
                  <Input
                    type="text"
                    border="1px solid #001E64 !important"
                    borderRadius="6px"
                    bg="white"
                    outline="none !important"
                    opacity="1 !important"
                    h="50px"
                    w="100%"
                    mt="10px"
                    placeholder="Digite o nome do seu produto"
                    maxLength={25}
                    value={tituloProduto}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTituloProduto(event.target.value);
                    }}
                  />
                  <Text fontSize="0.875rem">
                    {tituloProduto.length}/25 caracteres
                  </Text>
                </VStack>
                <Box width="100%" maxWidth="400px" m="0px 0 20px 0 !important">
                  {critica?.CRITICA_NOME_PRODUTO ? (
                    <BoxCritical textContent={critica?.CRITICA_NOME_PRODUTO} />
                  ) : undefined}
                </Box>
              </Box>
              <Box
                w="100%"
                bg="rgba(244, 244, 244, 0.7)"
                borderRadius="15px"
                p="30px"
                m="0 !important"
              >
                <Text fontSize="1rem">Digite a descrição do seu produto*</Text>
                <VStack alignItems="end" maxW="400px">
                  <Input
                    type="text"
                    border="1px solid #001E64 !important"
                    borderRadius="6px"
                    bg="white"
                    outline="none !important"
                    opacity="1 !important"
                    h="50px"
                    w="100%"
                    mt="10px"
                    placeholder="Digite a descrição do seu produto"
                    maxLength={50}
                    value={descricaoProduto}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setDescricaoProduto(event.target.value);
                    }}
                  />
                  <Text fontSize="0.875rem">
                    {descricaoProduto.length}/50 caracteres
                  </Text>
                </VStack>
                <Box width="100%" maxWidth="400px" m="0px 0 20px 0 !important">
                  {critica?.CRITICA_DESC_PRODUTO ? (
                    <BoxCritical textContent={critica?.CRITICA_DESC_PRODUTO} />
                  ) : undefined}
                </Box>
              </Box>
            </VStack>

            <VStack>
              <Heading
                w="100%"
                p="30px 0 20px"
                fontSize="1.25rem"
                fontWeight="700"
                lineHeight="2rem"
                color="#323232"
              >
                Personalize aqui sua remuneração e pagamento
              </Heading>
              <Stack bg="#FFFFFF" w="100%" m="0 !important">
                <HStack
                  mt="0 !important"
                  p="30px"
                  display="flex"
                  justifyContent="space-between"
                  bg="#F4F4F4"
                  gap="20px"
                  borderRadius="15px"
                >
                  <Box m="0px !important" w="100%" maxW="250px">
                    <Text
                      fontSize="1rem"
                      fontWeight="400"
                      lineHeight="1.5rem"
                      color="#202020"
                    >
                      Selecione a corretagem*
                    </Text>
                    <Select
                      id="Selecione a corretagem"
                      value={corretagemSelecionada}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setAgenciamentoSelecionado('');
                        setCorretagemSelecionada(e.target.value);
                      }}
                      mt="10px"
                      h="50px"
                      borderRadius="6px"
                      bg="#FFFFFF"
                      border="1px solid #001E64 !important"
                      placeholder="Corretagem"
                      _placeholder={{
                        color: '#828282',
                        fontSize: '1rem',
                        fontWeight: '400',
                        lineHeight: '1.5rem'
                      }}
                      _hover={{}}
                      _focus={{
                        '&:focus': {
                          boxShadow: 'none !important'
                        }
                      }}
                      _active={{}}
                    >
                      {corretagensProduto.map((option) => (
                        <option key={option} value={option}>
                          {option}%
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box m="0px !important" w="100%" maxW="250px">
                    <Text
                      fontSize="1rem"
                      fontWeight="400"
                      lineHeight="1.5rem"
                      color="#202020"
                    >
                      Selecione o agenciamento*
                    </Text>
                    <Select
                      id="Selecione o agenciamento"
                      value={agenciamentoSelecionado}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setAgenciamentoSelecionado(e.target.value);
                      }}
                      mt="10px"
                      h="50px"
                      disabled={
                        // TODO: useMemo
                        !(
                          getAgenciamentoByCorretagemAndProdType(
                            getProductType(),
                            Number(corretagemSelecionada)
                          )?.length > 0
                        )
                      }
                      borderRadius="6px"
                      bg="#FFFFFF"
                      border="1px solid #001E64 !important"
                      placeholder="Agenciamento"
                      _placeholder={{
                        color: '#828282',
                        fontSize: '1rem',
                        fontWeight: '400',
                        lineHeight: '1.5rem'
                      }}
                      _hover={{}}
                      _focus={{
                        '&:focus': {
                          boxShadow: 'none !important'
                        }
                      }}
                      _active={{}}
                    >
                      {
                        // TODO: useMemo
                        getAgenciamentoByCorretagemAndProdType(
                          getProductType(),
                          Number(corretagemSelecionada)
                        )?.map((item) => (
                          <option key={item} value={item}>
                            {item}%
                          </option>
                        ))
                      }
                    </Select>
                  </Box>
                  <Box
                    m="0px !important"
                    w="100%"
                    maxW="250px"
                    visibility="hidden"
                  ></Box>
                </HStack>
                <Box as="span" fontSize="12px" fontWeight="bold">
                  *Importante: Ao indicar um intermediador o percentual de
                  corretagem será compartilhado.
                </Box>
              </Stack>

              <Heading
                m="0 !important"
                w="100%"
                p="30px 0 20px"
                fontSize="1.25rem"
                fontWeight="700"
                lineHeight="2rem"
                color="#323232"
              >
                Parceiro
              </Heading>
              <Stack bg="#FFFFFF" w="100%" m="0 !important">
                <HStack
                  m="0 !important"
                  p="30px"
                  display="flex"
                  justifyContent="space-between"
                  bg="#F4F4F4"
                  gap="20px"
                  borderRadius="15px"
                >
                  <Box m="0px !important" w="100%" maxW="250px">
                    <Text
                      fontSize="1rem"
                      fontWeight="400"
                      lineHeight="1.5rem"
                      color="#202020"
                    >
                      Existe pagamento ao parceiro?
                    </Text>
                    <Stack direction="row">
                      {optionsForPartnerExistence.map((option) => {
                        return (
                          <Radio
                            key={option}
                            value={option}
                            isChecked={parceiro === option}
                            borderColor="#001E64"
                            colorScheme="orange"
                            _focus={{ outline: '0 !important' }}
                            onClick={() => {
                              handleSelectionForPartinerAndPercentage(option);
                            }}
                          >
                            {option}
                          </Radio>
                        );
                      })}
                    </Stack>
                  </Box>
                  {parceiro === 'Sim' && (
                    <Box m="0px !important" w="100%" maxW="250px">
                      <Text
                        fontSize="1rem"
                        fontWeight="400"
                        lineHeight="1.5rem"
                        color="#202020"
                      >
                        Escolha o parceiro cadastrado*
                      </Text>
                      <Select
                        id="Escolha o parceiro cadastrado"
                        value={evParceiro}
                        onChange={async (
                          e: React.ChangeEvent<HTMLSelectElement>
                        ) => {
                          const { value } = e.target;

                          setEvParceiro(Number(value));
                        }}
                        mt="10px"
                        h="50px"
                        borderRadius="6px"
                        bg="#FFFFFF"
                        border="1px solid #001E64 !important"
                        placeholder="Parceiro cadastrado"
                        _placeholder={{
                          color: '#828282',
                          fontSize: '1rem',
                          fontWeight: '400',
                          lineHeight: '1.5rem'
                        }}
                        _hover={{}}
                        _focus={{
                          '&:focus': {
                            boxShadow: 'none !important'
                          }
                        }}
                        _active={{}}
                      >
                        {dataListPartner?.map((item) => {
                          return (
                            <option
                              key={item.cod_estr_venda}
                              value={item.cod_estr_venda}
                            >
                              {item.cod_estr_venda} - {item.nome}
                            </option>
                          );
                        })}
                      </Select>
                    </Box>
                  )}
                  {parceiro === 'Sim' && (
                    <Box m="0px !important" w="100%" maxW="250px">
                      <Text
                        fontSize="1rem"
                        fontWeight="400"
                        lineHeight="1.5rem"
                        color="#202020"
                      >
                        Escolha o percentual*
                      </Text>
                      <Select
                        id="Escolha o percentual"
                        // TODO: Extrair logica
                        disabled={
                          !corretagemSelecionada ||
                          getIntermediacaoByCorretagem(
                            Number(corretagemSelecionada)
                          ).length <= 0 ||
                          !evParceiro
                        }
                        value={intermediacao}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setIntermediacao(e.target.value);
                        }}
                        mt="10px"
                        h="50px"
                        borderRadius="6px"
                        bg="#FFFFFF"
                        border="1px solid #001E64 !important"
                        placeholder="Percentual"
                        _placeholder={{
                          color: '#828282',
                          fontSize: '1rem',
                          fontWeight: '400',
                          lineHeight: '1.5rem'
                        }}
                        _hover={{}}
                        _focus={{
                          '&:focus': {
                            boxShadow: 'none !important'
                          }
                        }}
                        _active={{}}
                      >
                        {getIntermediacaoByCorretagem(
                          Number(corretagemSelecionada)
                        )?.map((item, index) => {
                          return (
                            <option key={index} value={item}>
                              {item} %
                            </option>
                          );
                        })}
                      </Select>
                    </Box>
                  )}
                </HStack>

                {alertErroParceiro && (
                  <Box
                    w="100%"
                    bg="#FACED2"
                    m="25px 0 0 0 !important"
                    p="15px"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    borderRadius="6px"
                  >
                    <Box display="flex" gridGap="10px" alignItems="center">
                      <WarningTwoIcon color="#d60404" w="18px" h="18px" />
                      <Text>
                        Não localizamos parceiros vinculados a sua estrutura,
                        contate o seu gestor de vendas e saiba como cadastrar
                      </Text>
                    </Box>

                    <CloseIcon onClick={() => setAlertErroParceiro(false)} />
                  </Box>
                )}
              </Stack>
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter
          padding="30px !important"
          display="flex"
          justifyContent="space-between"
          borderTop="1px solid #D2D2D2"
        >
          <Button
            background="#FFFFFF"
            margin="0px !import"
            width="152px"
            height="48px"
            borderRadius="24px"
            padding="13px 47px"
            gap="10px"
            border="1px solid #001E64"
            _hover={{}}
            _focus={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
            _active={{}}
            onClick={onClose}
          >
            <Text
              fontSize="1rem"
              lineHeight="1.375rem"
              fontWeight="700"
              color="#001E64"
            >
              Cancelar
            </Text>
          </Button>
          <Button
            background="#FF5000"
            margin="0px !import"
            width="204px"
            height="48px"
            borderRadius="133.5px"
            padding="13px 47px"
            gap="10px"
            border="1px solid transparent"
            disabled={
              alertErroParceiro || !isPartnersSectionComplete
                ? true
                : !configValida
            }
            _hover={{}}
            _focus={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
            _active={{}}
            onClick={onSubmit}
          >
            <Text
              fontSize="1rem"
              lineHeight="1.375rem"
              fontWeight="700"
              color="#FFFFFF"
            >
              Salvar
            </Text>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

function formatarParaMoeda(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}
